import React from 'react'

import { theme } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import styled from 'styled-components'

import {
  GIFTS_DETAIL_CONTAINER,
  GIFTS_DETAIL_HAS_GIFTS_TITLE,
} from 'utils/testing/testIDs'

import { GiftType } from '../../helpers'
import {
  DetailContainer,
  DetailHeader,
  SectionIcon,
  Title,
} from '../../shared/SectionDetails'
import { RECAP_XS_BREAKPOINT, fonts } from '../../styles'
import { NoGift } from './NoGift'

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media screen and (max-width: ${RECAP_XS_BREAKPOINT}px) {
    margin: auto;
    max-width: 320px;
  }
`

const ThanksText = styled.p<{ hasGifts: boolean }>`
  ${fonts.AN_14_600_22}
  color: ${theme.colors.beige};
  margin-bottom: ${(props) => (props.hasGifts ? 10 : 20)}px;
  width: 100%;
`

const EducationalLabel = styled.div`
  ${fonts.AN_14_600_22}
  align-items: center;
  color: ${theme.colors.beige};
  display: flex;
  height: 22px;
  width: 100%;
`

const getContributorsString = (gifts: GiftType[]): string => {
  return gifts.reduce((acc, cur, index) => {
    if (!acc) {
      return cur.contributorName
    }

    if (index + 1 === gifts.length) {
      return `${acc}, and ${cur.contributorName}`
    }

    return `${acc}, ${cur.contributorName}`
  }, '')
}

const Detail = ({
  beneficiaryName,
  contributionByOthers,
  gifts,
  investmentAccountId,
}: {
  beneficiaryName: string
  contributionByOthers: string
  gifts: GiftType[]
  investmentAccountId: string
}): React.ReactElement => {
  const { getRemoteString } = useRemoteStrings()
  const hasGifts = Boolean(gifts.length)

  return (
    <DetailContainer data-testid={GIFTS_DETAIL_CONTAINER}>
      <Content>
        <SectionIcon sectionIndex={1} />
        {hasGifts ? (
          <>
            <Title data-testid={GIFTS_DETAIL_HAS_GIFTS_TITLE}>
              {getRemoteString({
                key: 'early.recap.gifts.has_gifts.header',
              })}
            </Title>
            <DetailHeader isSmallFont={contributionByOthers.length > 8}>
              {contributionByOthers}
            </DetailHeader>
            <ThanksText hasGifts={hasGifts}>
              {getRemoteString({
                key: 'early.recap.gifts.has_gifts.subheader.variable',
                variables: [getContributorsString(gifts)],
              })}
            </ThanksText>
            <EducationalLabel>
              {getRemoteString({ key: 'early.recap.gifts.has_gifts.body' })}
            </EducationalLabel>
          </>
        ) : (
          <NoGift
            beneficiaryName={beneficiaryName}
            investmentAccountId={investmentAccountId}
          />
        )}
      </Content>
    </DetailContainer>
  )
}

export { Detail }
